import React, { useContext, useMemo, memo } from 'react';
import Dimensions from '@utils/dimensions';
import useDimensions from '@hooks/useDimensions';
import useHasMounted from '@hooks/useHasMounted';

// ant breakpoint
export const APP_LAYOUT_BREAKPOINTS = {
  SMALL: 576,
  MEDIUM: 768,
  LARGE: 992,
  XLARGE: 1200,
  XXLARGE: 1600,
};

export interface AppLayoutProviderProps {
  children?: React.ReactNode;
  initialIsMobile: boolean;
}

export interface AppLayoutProviderState {
  appOrientation: 'landscape' | 'portrait';
  deviceOrientation: 'landscape' | 'portrait';
  sizename: '1-sm' | '2-md' | '3-lg' | '4-xl' | '5-xxl' | '6-larger';
  isMobile: boolean;
}

const AppLayoutContext = React.createContext<AppLayoutProviderState>(getAppLayout());
AppLayoutContext.displayName = 'AppLayoutContext';

const AppLayoutController = memo(({ children, initialIsMobile }: AppLayoutProviderProps) => {
  const dimensions = useDimensions();
  const hasMounted = useHasMounted();

  const _appLayout = getAppLayout(dimensions);
  const appLayout = useMemo(() => {
    const value = getAppLayout(dimensions);
    return {
      ...value,
      isMobile: hasMounted ? value.isMobile : initialIsMobile,
    };
  }, [_appLayout.appOrientation, _appLayout.deviceOrientation, _appLayout.sizename, hasMounted, initialIsMobile]);

  return <AppLayoutContext.Provider value={appLayout}>{children}</AppLayoutContext.Provider>;
});

export function getAppLayout(dimensions?: { width: number; height: number }): AppLayoutProviderState {
  const { width, height } = dimensions || Dimensions.get('window');
  const sizename: AppLayoutProviderState['sizename'] =
    width <= APP_LAYOUT_BREAKPOINTS.SMALL
      ? '1-sm'
      : width <= APP_LAYOUT_BREAKPOINTS.MEDIUM
      ? '2-md'
      : width <= APP_LAYOUT_BREAKPOINTS.LARGE
      ? '3-lg'
      : width <= APP_LAYOUT_BREAKPOINTS.XLARGE
      ? '4-xl'
      : width <= APP_LAYOUT_BREAKPOINTS.XXLARGE
      ? '5-xxl'
      : '6-larger';

  const deviceOrientation = width > height ? 'landscape' : 'portrait';
  const appOrientation = deviceOrientation === 'landscape' || sizename >= '3-lg' ? 'landscape' : 'portrait';
  const isMobile = width <= APP_LAYOUT_BREAKPOINTS.MEDIUM;

  return { appOrientation, deviceOrientation, isMobile, sizename };
}

export function useAppLayout() {
  return useContext(AppLayoutContext);
}

export default AppLayoutController;
